import debounce from 'lodash.debounce'
import { useEffect, useMemo } from 'react'

type DebouncedCallback<T extends(...args: Array<any>) => any> = ReturnType<typeof debounce<T>>
type DebounceSettings = NonNullable<Parameters<typeof debounce>[2]>

/**
 * Helper hook to make a debounced callback by the delay given
 *
 * Be wary that this function will change each time
 * @param callback The function that will be called
 * @param delay The number of milliseconds the debounce will be
 * @param deps List of dependencies for when to update the callback function, otherwise it will be the version originally passed in
 * @param options Optional lodash debounce settings, such as a maximum wait time
 * @returns The debounced function, cancelled when its dependencies change or the component unmounts
 */
function useDebouncedCallback<T extends(...args: Array<any>) => any>(
  callback: T,
  delay: number,
  deps: Array<any>,
  options?: DebounceSettings,
): DebouncedCallback<T> {
  const debouncedCallback = useMemo(() => {
    return debounce(callback, delay, options)
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, options, ...deps])

  useEffect(() => {
    return () => debouncedCallback.cancel()
  }, [debouncedCallback])

  return debouncedCallback
}

export default useDebouncedCallback
